import { RudderstackIntegrations } from 'constants/common'
import { pageRoutes } from 'constants/routing'
import { usePathname } from 'next/navigation'
import { useEffect, useState } from 'react'
import {
  checkPathIsProviderProfileURL,
  checkPathIsSEFriendlyURL,
} from 'utils/common'

type RudderstackHookReturnType = {
  rudderStackLoaded: boolean
}

const useRudderStackInit = (): RudderstackHookReturnType => {
  const pathname = usePathname()

  // We need to load rudderstack only once perpage.
  const [rudderLoadedForPage, setRudderLoadedForPage] = useState<string>(null)
  const [rudderStackLoaded, setRudderStackLoaded] = useState<boolean>(false)

  useEffect(() => {
    // Needs to be loaded one time on load.
    import('@rudderstack/analytics-js').then(({ RudderAnalytics }) => {
      globalThis.rudderanalytics = new RudderAnalytics()
      setRudderStackLoaded(true)
    })
  }, [])

  useEffect(() => {
    // Load rudderstack perpage with required integration
    if (rudderStackLoaded && pathname) {
      loadRudderStack(globalThis.rudderanalytics)
    }
  }, [pathname, rudderStackLoaded])

  // Loades the rudderstack integration according to the page path.
  // Google ad should only load on provider profile  page and provider search page.
  const loadRudderStack = (rudderanalytics) => {
    const basePath = pathname.split('?')[0]

    if (rudderLoadedForPage === basePath) {
      // Exit function because rudder has already been loaded for this page.
      return
    }

    setRudderLoadedForPage(basePath)

    let load3rdPartyIntegrations = false

    if (
      basePath === pageRoutes.PROVIDER_SEARCH ||
      checkPathIsProviderProfileURL(pathname) ||
      checkPathIsSEFriendlyURL(pathname)
    ) {
      load3rdPartyIntegrations = true
    }

    if (
      process.env.NEXT_PUBLIC_RUDDER_TOKEN &&
      process.env.NEXT_PUBLIC_RUDDER_DATAPLANE_URL
    ) {
      rudderanalytics.load(
        process.env.NEXT_PUBLIC_RUDDER_TOKEN,
        process.env.NEXT_PUBLIC_RUDDER_DATAPLANE_URL,
        {
          integrations: {
            [RudderstackIntegrations.GOOGLE_ADS]: load3rdPartyIntegrations,
            [RudderstackIntegrations.FACEBOOK_PIXEL]: load3rdPartyIntegrations,
          },
        }
      )
    }
  }

  return { rudderStackLoaded }
}

export default useRudderStackInit
